import {
  PlayIcon,
  PauseIcon,
  PencilIcon,
  PlusSmIcon,
  TrashIcon,
  UserIcon,
  TagIcon,
  MailIcon,
  CalendarIcon,
  DocumentTextIcon,
} from '@heroicons/react/outline';
import { CONTACT_TYPE_LABELS } from '../PatientProfile/Account/Contacts/constants';
import { ContactType } from '../../../generated/graphql';
import Spinner from '../../svgs/Spinner';
import Button from '../../components/Button';
import { getTagStyles } from '../../lib/colors';
import { logger } from '../../../lib/logger';
import {
  ACTION_SCHEMA_BY_TYPE,
  DEFAULT_TAG_COLOR,
  TRIGGER_OPTIONS,
  getTimeTriggerLiveTagIds,
  isTimeBasedTrigger,
  parseTimeTriggerConfig,
} from './automationMetadata';
import { AutomationRule, AutomationRulePartial } from './types';
import {
  useFollowUpTemplatesQuery,
  useProviderAssessmentsWithoutScheduleQuery,
  useMeProviderProgramTagsQuery,
  useProviderServicesQuery,
} from '../../../generated/graphql';
import {
  ActionBadge,
  TimeBadge,
  TriggerBadge,
  WorkflowSummary,
} from './RuleListComponents';

type RuleExecutionStats = {
  total: number;
  success: number;
  failure: number;
  skipped: number;
  lastRunAt: string | null;
};

interface RuleListProps {
  automationRules: AutomationRule[] | AutomationRulePartial[];
  executionStatsByRuleId?: Record<string, RuleExecutionStats>;
  selectedRuleId: string | null;
  onCreateRule: () => void;
  onSelectRule: (ruleId: string) => void;
  onToggleRule: (rule: AutomationRule | AutomationRulePartial) => void;
  onEditRule: (rule: AutomationRule | AutomationRulePartial) => void;
  onDeleteRule: (ruleId: string) => void;
  /** When false, do not render the "Active Rules" heading and New rule button (used when parent renders "Your Automations" + Create Automation). */
  showHeader?: boolean;
  /** Tags created in rule editor; merged with programTags so list shows names before cache refetch */
  newlyCreatedTags?: Record<
    string,
    { id: string; name: string; color?: string | null }
  >;
}

/**
 * Returns base CSS classes for rule list items.
 * Note: Currently returns static classes; active/enabled states are handled via conditional styling elsewhere.
 */
const getClassNames = () => {
  return 'px-4 py-3 rounded-lg border cursor-pointer transition-colors';
};

/**
 * Formats a timestamp for display, returning "Never" if timestamp is missing or invalid.
 * Logs invalid timestamps to Sentry for monitoring.
 */
export const formatLastTriggered = (timestamp?: string | null) => {
  if (!timestamp) return 'Never';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    // Log invalid timestamp for monitoring (data corruption?)
    logger.warn('Invalid automation rule lastTriggered timestamp', {
      timestamp,
      ruleId: 'unknown', // Not available in this context
    });
    return 'Never';
  }
  return date.toLocaleString();
};

/**
 * Formats a creation date for display, returning empty string if date is missing or invalid.
 * Logs invalid dates to Sentry for monitoring.
 */
export function formatCreatedDate(createdAt?: string | null): string {
  if (!createdAt) return '';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    logger.warn('Invalid automation rule createdAt date', {
      createdAt,
    });
    return '';
  }
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

function getActionParamValue(
  action: {
    params?: Array<{ key?: string; value?: string }> | Record<string, string>;
  },
  key: string,
): string | undefined {
  const params = action.params;
  if (!params) return undefined;
  if (Array.isArray(params)) {
    const entry = params.find((p) => p.key === key);
    return entry?.value;
  }
  return params[key];
}

/**
 * Formats a rule's trigger event into a human-readable label with icon and optional tag info.
 *
 * @param rule - The automation rule to format
 * @param programTags - Optional list of available program tags for name/color lookup
 * @returns An object with label text, icon component, and optional tag metadata
 *
 * @remarks
 * - Special handling for 'client_created' trigger shows "When client is created"
 * - Tag-based triggers ('client_tag_added', 'client_tag_removed') extract tag info from conditions.rules[0].value
 * - Falls back to trigger event name if no mapping exists in TRIGGER_OPTIONS
 * - For service_booked, includes the selected provider service name when available
 */
function getProviderServiceIdFromRule(
  rule: AutomationRule | AutomationRulePartial,
): string | undefined {
  const serviceId = rule.conditions?.rules?.find(
    (r) => r.field === 'provider_service_id',
  )?.value;
  const trimmed = serviceId?.trim();
  return trimmed || undefined;
}

export function getWorkflowTriggerLabel(
  rule: AutomationRule | AutomationRulePartial,
  programTags?: Array<{ id: string; name: string; color?: string | null }>,
  providerServices?: Array<{ id: string; name: string }>,
): {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  labelDetail?: string;
  tagName?: string;
  tagColor?: string;
  andTags?: Array<{ name: string; color?: string }>;
} {
  const triggerOption = TRIGGER_OPTIONS.find(
    (o) => o.value === rule.triggerEvent,
  );
  const label = triggerOption?.label ?? rule.triggerEvent ?? '—';
  const isTagTrigger =
    rule.triggerEvent === 'client_tag_added' ||
    rule.triggerEvent === 'client_tag_removed' ||
    rule.triggerEvent === 'client_tag_changed';
  if (rule.triggerEvent === 'client_created') {
    return { label: `Client is created`, icon: UserIcon };
  }
  if (rule.triggerEvent === 'practitioner_assigned') {
    return { label: `Practitioner assigned to client`, icon: UserIcon };
  }
  if (isTagTrigger) {
    const firstTagId = rule.conditions?.rules?.[0]?.value?.trim?.();
    const tag = firstTagId && programTags?.find((t) => t.id === firstTagId);
    return {
      label: `Tag is applied to client`,
      icon: TagIcon,
      tagName: tag?.name ?? (firstTagId || undefined),
      tagColor: tag?.color ?? undefined,
    };
  }
  if (isTimeBasedTrigger(rule.triggerEvent)) {
    const config = parseTimeTriggerConfig(rule.triggerConfig);
    const offset = config?.offset ?? 0;
    const tagIds = getTimeTriggerLiveTagIds(rule);
    const andTags =
      tagIds.length > 0
        ? {
            andTags: tagIds.map((tagId) => {
              const tag = programTags?.find((t) => t.id === tagId);
              return {
                name: tag?.name ?? tagId,
                color: tag?.color ?? undefined,
              };
            }),
          }
        : {};
    if (offset === 0) {
      return { label, icon: CalendarIcon, ...andTags };
    }
    const absDays = Math.abs(offset);
    const dayLabel = absDays === 1 ? 'day' : 'days';
    const direction = offset < 0 ? 'before' : 'after';
    return {
      label: `${absDays} ${dayLabel} ${direction} ${label}`,
      icon: CalendarIcon,
      ...andTags,
    };
  }
  if (rule.triggerEvent === 'service_booked') {
    const serviceId = getProviderServiceIdFromRule(rule);
    const service =
      serviceId && providerServices?.find((s) => s.id === serviceId);
    const serviceName = service?.name;
    return {
      label: 'Service booked',
      labelDetail: serviceName,
      icon: CalendarIcon,
    };
  }
  return { label: label.toLowerCase(), icon: TagIcon };
}

export type ActionDisplayItem = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  templateName?: string;
  tagName?: string;
  tagColor?: string;
  formName?: string;
  contactTypeLabel?: string;
};

/**
 * Returns a summary of the first action for a rule (for list display).
 *
 * @param rule - The automation rule
 * @param mailTemplates - Optional list of mail templates for name lookup
 * @returns The first action's display info, or a default "No action" placeholder
 *
 * @remarks
 * - Filters out 'call_webhook' actions from display
 * - For send_template actions, includes the template name
 * - For assign_tag actions, includes the tag name and color
 */
export function getWorkflowActionLabel(
  rule: AutomationRule | AutomationRulePartial,
  mailTemplates?: Array<{ id: string; name?: string | null }>,
  programTags?: Array<{ id: string; name: string; color?: string | null }>,
  practitionerForms?: Array<{ id: string; name?: string | null }>,
): ActionDisplayItem {
  const first = getWorkflowActionDisplays(
    rule,
    mailTemplates,
    programTags,
    practitionerForms,
  )[0];
  return first ?? { label: 'No action', icon: MailIcon };
}

/**
 * Returns display information for all actions in a rule.
 *
 * @param rule - The automation rule
 * @param mailTemplates - Optional list of mail templates for name lookup
 * @param programTags - Optional list of program tags for name/color lookup
 * @returns Array of action display items with label, icon, and optional metadata
 *
 * @remarks
 * - Filters out 'call_webhook' actions from display
 * - For send_template actions, includes the template name
 * - For assign_tag actions, includes the tag name and color
 * - Each action includes an appropriate icon for its type
 */
export function getWorkflowActionDisplays(
  rule: AutomationRule | AutomationRulePartial,
  mailTemplates?: Array<{ id: string; name?: string | null }>,
  programTags?: Array<{ id: string; name: string; color?: string | null }>,
  practitionerForms?: Array<{ id: string; name?: string | null }>,
): ActionDisplayItem[] {
  const actions = (rule.actions ?? []).filter(
    (a) => a?.type && a.type !== 'call_webhook',
  );
  return actions.map((action) => {
    const schema = ACTION_SCHEMA_BY_TYPE[action.type!];
    const baseLabel = schema?.label ?? action.type ?? '—';
    if (action.type === 'send_template') {
      const templateId = getActionParamValue(action, 'follow_up_template_id');
      const template =
        templateId && mailTemplates?.find((t) => t.id === templateId);
      return {
        label: baseLabel,
        icon: MailIcon,
        templateName: template?.name ?? (templateId || undefined),
      };
    }
    if (action.type === 'assign_tag') {
      const rawTagIds =
        getActionParamValue(action, 'tag_ids') ||
        getActionParamValue(action, 'tag_id') ||
        '';
      const tagId = rawTagIds.split(',')[0]?.trim();
      const tag = tagId && programTags?.find((t) => t.id === tagId);
      return {
        label: baseLabel,
        icon: TagIcon,
        tagName: tag?.name ?? (tagId || undefined),
        tagColor: tag?.color ?? undefined,
      };
    }
    if (action.type === 'send_contact_form') {
      const assessmentId = getActionParamValue(action, 'assessment_id');
      const form =
        assessmentId && practitionerForms?.find((f) => f.id === assessmentId);
      const contactTypeRaw = getActionParamValue(action, 'contact_type');
      const contactLabel =
        contactTypeRaw && CONTACT_TYPE_LABELS[contactTypeRaw as ContactType]
          ? CONTACT_TYPE_LABELS[contactTypeRaw as ContactType]
          : contactTypeRaw || undefined;
      return {
        label: baseLabel,
        icon: DocumentTextIcon,
        formName: form?.name ?? undefined,
        contactTypeLabel: contactLabel,
      };
    }
    return { label: baseLabel, icon: MailIcon };
  });
}

/**
 * Generates a human-readable summary of a rule's trigger, conditions, and actions.
 *
 * @param rule - The automation rule to summarize
 * @returns A string like "[trigger], if [N] condition(s), then [action1, action2]"
 *
 * @remarks
 * - Filters out 'call_webhook' actions from the summary
 * - Uses TRIGGER_OPTIONS for readable trigger labels
 * - Shows action labels from ACTION_SCHEMA_BY_TYPE
 * - Omits the conditions part if there are no conditions
 */
export function getRuleSummary(
  rule: AutomationRule | AutomationRulePartial,
): string {
  const triggerLabel =
    TRIGGER_OPTIONS.find((o) => o.value === rule.triggerEvent)?.label ||
    rule.triggerEvent ||
    '—';
  const conditionCount = rule.conditions?.rules?.length ?? 0;
  const actionTypes = (rule.actions || [])
    .filter((a) => a?.type && a.type !== 'call_webhook')
    .map((a) => ACTION_SCHEMA_BY_TYPE[a.type!]?.label || a.type);
  const thenPart =
    actionTypes.length > 0 ? actionTypes.join(', ') : 'no actions';
  const ifPart =
    conditionCount > 0 ? `, if ${conditionCount} condition(s)` : '';
  return `${triggerLabel}${ifPart}, then ${thenPart}`;
}

export default function RuleList({
  automationRules,
  executionStatsByRuleId = {},
  selectedRuleId,
  onCreateRule,
  onSelectRule,
  onToggleRule,
  onEditRule,
  onDeleteRule,
  showHeader = true,
  newlyCreatedTags = {},
}: RuleListProps) {
  const { data: programTagsData, loading: tagsLoading } =
    useMeProviderProgramTagsQuery();
  const { data: followUpTemplatesData, loading: templatesLoading } =
    useFollowUpTemplatesQuery({
      variables: { input: { isDraft: false } },
      fetchPolicy: 'cache-first',
    });
  const { data: providerServicesData, loading: servicesLoading } =
    useProviderServicesQuery();
  const programTagsFromQuery =
    programTagsData?.meProvider?.provider.programTags ?? [];
  const programTags = [
    ...Object.values(newlyCreatedTags),
    ...programTagsFromQuery.filter((t) => !newlyCreatedTags[t.id]),
  ];
  const mailTemplates = followUpTemplatesData?.followUpTemplates ?? [];
  const { data: providerAssessmentsData, loading: formsLoading } =
    useProviderAssessmentsWithoutScheduleQuery({
      fetchPolicy: 'cache-first',
    });
  const practitionerOnlyForms = (providerAssessmentsData?.forms ?? []).filter(
    (f) => f.completableByProviderOnly === true,
  );
  const providerServices = providerServicesData?.providerServices ?? [];

  // Wait for tag, template, service, and form data before rendering rule cards to avoid
  // briefly showing IDs instead of names.
  const isReady =
    (!tagsLoading || !!programTagsData) &&
    (!templatesLoading || !!followUpTemplatesData) &&
    (!servicesLoading || !!providerServicesData) &&
    (!formsLoading || !!providerAssessmentsData);

  return (
    <div className="w-full">
      {showHeader && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="font-sans text-category font-semibold text-neutral-150">
            Active Rules ({automationRules.filter((r) => r.enabled).length})
          </h2>
          <Button
            title="Create automation"
            size="medium"
            onClick={onCreateRule}
            IconComponent={PlusSmIcon}
            iconPosition="left"
          />
        </div>
      )}

      {!isReady ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <Spinner className="h-8 w-8 animate-spin text-green-150" />
        </div>
      ) : automationRules.length === 0 ? (
        <div className="flex h-full items-center justify-center p-8">
          <div className="text-center">
            <p className="text-body text-neutral-110">No automations yet.</p>
            <p className="text-sm mt-2 text-neutral-100">
              Create your first automation to get started.
            </p>
            <Button
              theme="primary"
              size="medium"
              title="Create automation"
              IconComponent={PlusSmIcon}
              iconPosition="left"
              className="mt-4"
              onClick={onCreateRule}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {automationRules.map((rule) => {
            const triggerDisplay = getWorkflowTriggerLabel(
              rule,
              programTags,
              providerServices,
            );
            const actionDisplays = getWorkflowActionDisplays(
              rule,
              mailTemplates,
              programTags,
              practitionerOnlyForms,
            );
            const createdStr = formatCreatedDate(rule.createdAt);
            const triggerTagStyles = triggerDisplay.tagName
              ? getTagStyles(triggerDisplay.tagColor ?? DEFAULT_TAG_COLOR)
              : null;

            return (
              <div
                key={rule.id}
                onClick={() => onEditRule(rule)}
                className={getClassNames()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onEditRule(rule);
                  }
                }}
                aria-label={`${rule.name}, ${
                  rule.enabled ? 'Active' : 'Inactive'
                }${createdStr ? `, created ${createdStr}` : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-medium text-neutral-150">
                      {rule.name}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11.5px] font-medium ${
                          rule.enabled
                            ? 'bg-green-100 text-white'
                            : 'bg-neutral-75 text-neutral-125'
                        }`}
                      >
                        {rule.enabled ? 'Active' : 'Inactive'}
                      </span>
                      {createdStr && (
                        <span className="text-[11.5px] text-neutral-110">
                          Created {createdStr}
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className="flex shrink-0 flex-wrap items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      title={rule.enabled ? 'Disable' : 'Enable'}
                      theme="secondary"
                      size="small"
                      IconComponent={rule.enabled ? PauseIcon : PlayIcon}
                      iconPosition="left"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleRule(rule);
                      }}
                    />
                    <Button
                      title="Edit"
                      theme="secondary"
                      size="small"
                      IconComponent={PencilIcon}
                      iconPosition="left"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditRule(rule);
                      }}
                    />
                    <Button
                      title="Delete"
                      theme="destructive"
                      size="small"
                      IconComponent={TrashIcon}
                      iconPosition="left"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Use server ID if available (saved rule), otherwise client ID (temp rule)
                        onDeleteRule(rule.automationRuleId || rule.id!);
                      }}
                    />
                  </div>
                </div>
                <WorkflowSummary
                  timePill={
                    isTimeBasedTrigger(rule.triggerEvent)
                      ? (() => {
                          const config = parseTimeTriggerConfig(
                            rule.triggerConfig,
                          );
                          return config?.time ? (
                            <TimeBadge time={config.time} />
                          ) : undefined;
                        })()
                      : undefined
                  }
                  triggerBadge={
                    <TriggerBadge
                      label={triggerDisplay.label}
                      icon={triggerDisplay.icon}
                      labelDetail={triggerDisplay.labelDetail}
                      tagName={triggerDisplay.tagName}
                      tagStyles={triggerTagStyles ?? undefined}
                      andTags={triggerDisplay.andTags}
                    />
                  }
                  actionBadges={actionDisplays.map((action, idx) => (
                    <ActionBadge key={idx} action={action} index={idx} />
                  ))}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
